// Shared formatting for risk and performance figures.
//
// The rule these enforce, from ARCHITECTURE.md invariant 8: a risk or
// performance number comes from the API or is VISIBLY ABSENT — never silently
// defaulted. A plausible number is indistinguishable from a real result, which
// is how a backtest with no drawdown data once displayed "0.00%" in green
// (0 <= 0.05 scores success.main), i.e. missing risk data looked risk-free.
//
// ⚠️ `null * 100 === 0` in JavaScript, so an absent value becomes a zero the
// moment it meets arithmetic. Every render of these figures must go through
// these helpers rather than inline maths.

export const ABSENT = '—';

// Non-finite counts as absent. Three reasons in one guard:
//   • a non-NUMBER has no .toFixed and would throw inside a render;
//   • NaN formats as "NaN%", which is a defect leaking to the user;
//   • ±Infinity formats as "Infinity%", which is worse — it looks like a value.
// ⚠️ Product note: a profit factor of Infinity is arguably MEANINGFUL (gross
// profit with zero losses). It renders as absent here deliberately, because
// "Infinity" in a metric tile reads as a bug rather than a result. If that
// should display as ∞ instead, it is a display decision — make it here, once.
export const isAbsent = (v) => typeof v !== 'number' || !Number.isFinite(v);
//
// ⚠️ Deliberate trade-off: a NUMERIC STRING ('1.5') is treated as absent and
// renders as "—". That is a behaviour change — previously `'1.5' * 100` would
// have quietly worked. It is the right way round: if an endpoint starts
// returning stringified numbers, a visible gap surfaces the contract break,
// whereas coercion would hide it and re-admit exactly the leniency that let
// silent zeros through. Do NOT add a Number() coercion here; fix the producer.

export const formatPercentage = (value, dp = 2) =>
  isAbsent(value) ? ABSENT : `${(value * 100).toFixed(dp)}%`;

export const formatNumber = (value, dp = 2) =>
  isAbsent(value) ? ABSENT : value.toFixed(dp);

export const formatCurrency = (amount, opts = {}) =>
  isAbsent(amount)
    ? ABSENT
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        ...opts,
      }).format(amount);

// Absent is not good, bad or middling — it is unknown, and must not carry a
// judgement colour.
export const absentColor = 'text.disabled';
